import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useReporter } from '../../reporter';
import { ProductSelectionForm } from '../ProductSelectionForm';
import { ApiResult } from '../../api';
import { useSessionCustomer } from '../../customer';
import {
  useExperiment,
  HIDE_BAKED_PLAN_TYPE_KEY,
  SHOW_HALF_FRESH,
} from '../../experiments';
import { userViewMealPrepScreen, userUpdatesMealPrep } from './events';

export const PlanTypeSelectionForm = ({
  onSubmit,
  headline,
  subheadline,
  ctaText,
  planTypes,
  collapsedPlanTypes,
  defaultPlanType,
  unavailablePlanTypes = [],
  goBack,
  period = '',
  legend = '',
}) => {
  const reporter = useReporter();

  const { value: hideBakedPlan } = useExperiment(
    HIDE_BAKED_PLAN_TYPE_KEY,
    false,
  );

  const { value: showHalfFresh } = useExperiment(SHOW_HALF_FRESH, false);

  const isBakedPlan = plan => plan.value === 'baked';
  const isNotBakedPlan = plan => plan.value !== 'baked';
  const isHalfFreshPlan = plan => plan.value === 'half_fresh';
  const isNotHalfFreshPlan = plan => plan.value !== 'half_fresh';

  if (
    planTypes.filter(isBakedPlan).length &&
    collapsedPlanTypes.filter(isBakedPlan).length
  ) {
    if (hideBakedPlan) {
      planTypes = planTypes.filter(isNotBakedPlan);
    } else {
      // checking this because we do not want this to filter if the flag is turn off and returns as undefined or null
      collapsedPlanTypes = collapsedPlanTypes.filter(isNotBakedPlan);
    }
  }

  if (planTypes.filter(isHalfFreshPlan).length && !showHalfFresh) {
    planTypes = planTypes.filter(isNotHalfFreshPlan);
  }

  const [selectedPlanType, setSelectedPlanType] = useState(defaultPlanType);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { customer, isLoading: isCustomerLoading } = useSessionCustomer();

  useEffect(() => {
    if (!isCustomerLoading) {
      reporter.tag(
        userViewMealPrepScreen({
          customer_info: {
            email: customer.email,
          },
        }),
      );
    }
  }, [reporter, isCustomerLoading, customer]);

  const onChange = product => {
    if (unavailablePlanTypes.includes(product[0])) return null;
    setSelectedPlanType(product[0]);
  };

  const onSubmitHandler = async event => {
    event.preventDefault();
    setIsSubmitting(true);
    reporter.tag(userUpdatesMealPrep(selectedPlanType));
    const submitResult = await onSubmit(selectedPlanType);
    ApiResult.ifError(submitResult, () => setIsSubmitting(false));
  };

  const checkoutBarProps = {
    ctaText,
    isSubmitDisabled: isSubmitting || !selectedPlanType,
    isBackButtonDisabled: isSubmitting,
    period,
    shown: true,
    onBackButtonClick: goBack,
    isSubmitting: isSubmitting,
  };

  const getConfiguredPlanTypes = planTypes =>
    planTypes.map(planType => ({
      ...planType,
      shouldShowMoreDetails: false,
      inputType: 'radio',
    }));

  const productSelectorProps = {
    products: getConfiguredPlanTypes(planTypes),
    collapsedProducts: getConfiguredPlanTypes(collapsedPlanTypes),
    selected: [selectedPlanType],
    unavailableProducts: unavailablePlanTypes,
    variant: 'radio',
    period,
    productCardVariant: 'cro',
    direction: 'vertical',
  };

  return (
    <>
      <ProductSelectionForm
        checkoutBar={checkoutBarProps}
        headline={headline}
        legend={legend}
        onChange={onChange}
        onSubmit={onSubmitHandler}
        productSelector={productSelectorProps}
        subheadline={subheadline}
      />
    </>
  );
};
PlanTypeSelectionForm.displayName = 'PlanTypeSelectionForm';
PlanTypeSelectionForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  headline: PropTypes.string.isRequired,
  subheadline: PropTypes.node.isRequired,
  ctaText: PropTypes.string.isRequired,
  unavailablePlanTypes: PropTypes.array,
  planTypes: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      description: PropTypes.node.isRequired,
      img: PropTypes.any,
      pricingInfo: PropTypes.shape({
        discount: PropTypes.string.isRequired,
        price: PropTypes.string.isRequired,
      }),
      benefit: PropTypes.string,
    }).isRequired,
  ).isRequired,
  collapsedPlanTypes: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      description: PropTypes.node.isRequired,
      img: PropTypes.any,
      pricingInfo: PropTypes.shape({
        discount: PropTypes.string.isRequired,
        price: PropTypes.string.isRequired,
      }),
      benefit: PropTypes.string,
    }).isRequired,
  ).isRequired,
  defaultPlanType: PropTypes.string,
  goBack: PropTypes.func.isRequired,
  period: PropTypes.string,
  legend: PropTypes.string,
};
